import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Divider, Stack } from '@moda/om';
import { useEnableClubModa } from '../../../hooks/useEnableClubModa';
import { DesktopNavigationMainContentCategory } from '../DesktopNavigationMainContentCategory';
import { DesktopNavigationPulldown } from '../DesktopNavigationPulldown';
import { DesktopSiteNavItemLoading } from '../../SiteNav/DesktopSiteNav/DesktopSiteNavItemLoading';
import { useDesktopNavigationContext } from '../DesktopNavigationProvider';

import './DesktopNavigationMainContent.scss';

export const DesktopNavigationMainContent = () => {
  const enableClubModa = useEnableClubModa();
  const { selectedVerticalData, selectedVerticalId, verticals } = useDesktopNavigationContext();

  const [activeCategory, setActiveCategory] = useState<string>();
  const pulldownRef = useRef<HTMLDivElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);

  // When user presses Arrow Down, jump focus into the dropdown
  const handleArrowDown = () => {
    requestAnimationFrame(() => {
      const firstLink = pulldownRef.current?.querySelector('a');
      firstLink?.focus();
    });
  };

  useEffect(() => {
    if (!activeCategory) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveCategory(undefined);
        lastTriggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeCategory]);

  const pulldownContent = useMemo(() => {
    if (!selectedVerticalData || !selectedVerticalId || !activeCategory) return;
    const content = selectedVerticalData.items.find(item => item.id === activeCategory);
    if (!content?.units.length) return;
    return content;
  }, [activeCategory, selectedVerticalData, selectedVerticalId]);

  if (!verticals) return;

  return (
    <div
      className={classNames('DesktopNavigationMainContent', {
        'DesktopNavigationMainContent--club': enableClubModa
      })}
      data-testid="DesktopNavigationMainContent"
      role="navigation"
      aria-label="Main navigation"
      onMouseLeave={() => setActiveCategory(undefined)}
    >
      <Stack className="DesktopNavigationMainContent__items" space={3}>
        <Divider className="DesktopNavigationMainContent__divider" />

        {!selectedVerticalData && <DesktopSiteNavItemLoading />}

        {selectedVerticalData && (
          <Stack
            direction="horizontal"
            className="DesktopNavigationMainContent__item"
            justifyContent="center"
            space={5}
          >
            {selectedVerticalData.items.map(item => (
              <DesktopNavigationMainContentCategory
                key={item.id}
                category={item}
                selected={activeCategory === item.id}
                onSelect={trigger => {
                  setActiveCategory(item.id);
                  lastTriggerRef.current = trigger;
                }}
                onArrowDown={handleArrowDown}
                onClickCategory={() => setActiveCategory(undefined)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {pulldownContent && (
        <DesktopNavigationPulldown
          ref={pulldownRef}
          content={pulldownContent}
          onHide={() => setActiveCategory(undefined)}
        />
      )}
    </div>
  );
};
